import React from "react";
import classNames from "classnames";

import Icon from "components/icon/Icon";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import "components/tabs/tabs.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";

type Props<T> = {
  tabs: GenericTab<T>[];
  secondaryTabs?: SecondaryTab[];
  selected: number;
  onSelect: (index: number) => void;
  fitted?: boolean;
  children?: any;
  errorTabs?: number[];
  fullWidth?: boolean;
  variant?: "default" | "underline" | "wide"; // Add variant prop
};

// Generate compatible tabs type from an optionally given type.
export type GenericTab<T = Record<string, any>> = T & {
  content: string | Element | JSX.Element;
  counter?: number;
  disabled?: boolean;
};

type SecondaryTab = {
  icon: IconProp;
  onSelect: (index: number) => void;
};

function Tabs<T>(props: Props<T>) {
  const variant = props.variant || "default"; // Default to "default" variant

  return (
    <div>
      <div className="ui-tabs_wrapper">
        <ul
          className={classNames("ui-tabs", {
            "ui-tabs--fitted": props.fitted,
            "ui-tabs--underline": variant === "underline" || variant === "wide", // Add variant class
            "ui-tabs--wide": variant === "wide",
          })}
        >
          <div className={props.fullWidth ? "ui-tabs_full" : `ui-tabs_primary ${variant === "underline" || variant === "wide" ? "ui-spacing-tabs" : ""}`}>
            {props.tabs.map((tab, index: number) => (
              <li className="ui-tabs_tab-container" key={index}>
                {props.errorTabs?.includes(index) ? (
                  <button
                    type="button"
                    className={classNames("ui-tabs_tab", {
                      "ui-tabs_tab--selected": props.selected === index,
                      "ui-tabs_tab--disabled": tab.disabled ?? false,
                      "ui-tabs_tab--underline": variant === "underline" || variant === "wide", // Add variant class
                    })}
                    onClick={() => props.onSelect(index)}
                    disabled={tab.disabled ?? false}
                  >
                    <span className="ui-tabs_title" style={{ padding: "0.5rem 1.6rem" }}>
                      {tab.content}
                      &nbsp;
                      <FontAwesomeIcon icon={faExclamationCircle} />
                      <span className="ui-tabs_counter">{tab.counter}</span>
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className={classNames("ui-tabs_tab", {
                      "ui-tabs_tab--selected": props.selected === index,
                      "ui-tabs_tab--disabled": tab.disabled ?? false,
                      "ui-tabs_tab--underline": variant === "underline" || variant === "wide", // Add variant class
                    })}
                    onClick={() => props.onSelect(index)}
                    disabled={tab.disabled ?? false}
                  >
                    <span className={classNames({ "ui-tabs_title": variant !== "underline" && variant !== "wide" })}>
                      {tab.content}
                      <span className="ui-tabs_counter">{tab.counter}</span>
                    </span>
                  </button>
                )}
              </li>
            ))}
          </div>
          <div className="ui-tabs_secondary">
            {props.secondaryTabs ? (
              <>
                {props.secondaryTabs.map((tab, index: number) => (
                  <li className="ui-tabs_tab-container" key={index}>
                    <button type="button" onClick={() => tab.onSelect(index)}>
                      <span className="ui-tabs_title-secondary">
                        <Icon icon={tab.icon} />
                      </span>
                    </button>
                  </li>
                ))}
              </>
            ) : null}
          </div>
        </ul>
      </div>
      <div className="ui-tabs_panel">{props.children}</div>
    </div>
  );
}

export default Tabs;